import mongoose, { Mongoose } from "mongoose";
import { UtmServiceInterface } from "../interfaces/UtmServiceInterface";
import PartnerSchema from "../schemas/Partner";
import {
  GetPartnerData,
  GetPartnerParamsType,
  PartnerDataType,
  PartnerVerticalDataType,
  UtmVerticalListType,
} from "../types/utm-service.types";

class UtmService implements UtmServiceInterface {
  db: Mongoose = {} as Mongoose;

  constructor(mongoUrl: string) {
    this.connectToMongo(mongoUrl)
      .then(() => console.log("UtmService connected successfully!"))
      .catch((e) =>
        console.log("Failed to connect UtmServices to Mongo DB: ", e)
      );
  }

  async connectToMongo(mongoUrl: string) {
    try {
      this.db = await mongoose.connect(mongoUrl || "");
    } catch (e) {
      console.log("Failed to connect UtmServices to Mongo DB: ", e);
    }
  }

  private generateUtmData(
    utmParams: GetPartnerParamsType,
    verticals: string[]
  ): UtmVerticalListType {
    const verticalList: UtmVerticalListType = {};
    verticals.map((verticalName: string) => {
      verticalList[verticalName] = {
        source: utmParams.utmSource || "Direct",
        campaign: utmParams.utmCampaign || null,
        medium: utmParams.utmMedium || null,
        content: utmParams.utmContent || null,
        term: utmParams.utmTerm || null,
        transaction: utmParams.utmTransaction || null,
        customer: utmParams.utmCustomer || null,
      };
    });
    return verticalList;
  }

  private static getPartnerExpiration(partnerData: PartnerDataType) {
    const { expires, paymentExpire } = partnerData;

    if (expires === 0) {
      return { date: null, paymentExpire };
    }

    const date = new Date();
    date.setDate(date.getDate() + expires);

    return { date, paymentExpire };
  }

  private generatePartnerData(
    utmParams: GetPartnerParamsType,
    partnerData: PartnerDataType
  ): PartnerVerticalDataType {
    const expires = UtmService.getPartnerExpiration(partnerData);
    const partnerVerticalData: PartnerVerticalDataType = {};
    partnerData.vertical.map((verticalName) => {
      partnerVerticalData[verticalName] = {
        source: utmParams.utmSource || "Direct",
        agentId: partnerData.agentId,
        transaction: utmParams.utmTransactionId || null,
        expires,
        scope: partnerData.scope,
        platinum: partnerData.platinum,
        filters: null,
        partnerId: partnerData.partnerId
      };
    });
    return partnerVerticalData;
  }

  private getDefaultPartnerData(
    utmParams: GetPartnerParamsType,
    defaultVerticals: string[]
  ): GetPartnerData {
    const defaultUtmParams: GetPartnerParamsType = {
      ...utmParams,
      utmSource: "Direct",
    };
    const partnerDataDefault: PartnerDataType = {
      name: 'Direct',
      expires: 0,
      scope: "global",
      vertical: defaultVerticals,
      platinum: true,
      paymentExpire: false,
      partnerId: null,
      agentId: null,
      hasHomepage: false,
    };
    return {
      partnerData: partnerDataDefault,
      cookie: {
        utm: this.generateUtmData(
          defaultUtmParams,
          partnerDataDefault.vertical
        ),
      },
      session: {
        name: partnerDataDefault.name,
        platinum: partnerDataDefault.platinum,
        hasHomepage: partnerDataDefault.hasHomepage || false,
        partner: this.generatePartnerData(defaultUtmParams, partnerDataDefault),
      },
    };
  }

  toCamelCame = (myString: string) => myString.replace(/_([a-z])/g, function (g) { return g[1].toUpperCase(); });

  async getPartner(
    utmParams: GetPartnerParamsType,
    defaultVerticals: string[]
  ) {
    const utmParamsLocal = {...utmParams};
    if ("utm_source" in utmParams) {
      // ts-ignore
      Object.keys(utmParams).forEach((key) => {
        utmParamsLocal[this.toCamelCame(key) as keyof GetPartnerParamsType] = utmParams[key as keyof GetPartnerParamsType] as string || null;
      });
    }
    if (this.db && utmParamsLocal.utmSource && utmParamsLocal.utmSource === "Direct")
      return this.getDefaultPartnerData(utmParamsLocal, defaultVerticals);

    // Will search for the partner on database if the utmSource is different from 'Direct'
    const PartnerModel = mongoose.model("Partners", PartnerSchema);
    const partnerData: PartnerDataType | null = await PartnerModel.findOne({
      name: utmParamsLocal.utmSource || "",
    });

    if (!partnerData)
      return this.getDefaultPartnerData(utmParams, defaultVerticals);

    const utm = this.generateUtmData(utmParamsLocal, partnerData.vertical);
    const partner = this.generatePartnerData(utmParamsLocal, partnerData);
    return {
      partnerData,
      cookie: {
        utm,
      },
      session: {
        name: partnerData.name,
        platinum: partnerData.platinum,
        hasHomepage: partnerData.hasHomepage || false,
        partner,
      },
    };
  }
}

export default (mongoUrl: string) => new UtmService(mongoUrl);
